import { useState, useRef } from "react";
import { Container, Card, Form, Button, Navbar, Alert } from "react-bootstrap";
import { FiLock } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import api from "../utils/helper";

const VerifyOtp = () => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const navigate = useNavigate();
  const location = useLocation();

  const phoneNumber = location.state?.phoneNumber || "";

  const verifyOtpMutation = useMutation(
    (verifyData) => api.post("/auth/verify-otp", verifyData),
    {
      onSuccess: (response) => {
        console.log("OTP verified successfully:", response.data);
        setError("");
        navigate("/dashboard", {
          state: {
            user: response.data.user,
          },
        });
      },
      onError: (error) => {
        console.error("Error verifying OTP:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Invalid OTP. Please try again.";
        setError(errorMessage);
      },
    }
  );

  const resendOtpMutation = useMutation(
    (otpData) => api.post("/auth/send-otp", otpData),
    {
      onSuccess: (response) => {
        console.log("OTP resent successfully:", response.data);
        setError("");
        setOtp(["", "", "", ""]);
        inputRefs[0].current.focus();
      },
      onError: (error) => {
        console.error("Error resending OTP:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to resend OTP. Please try again.";
        setError(errorMessage);
      },
    }
  );

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (error) setError("");

    if (value && index < 3) {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const handleVerify = (e) => {
    if (e) e.preventDefault();

    const otpCode = otp.join("");

    if (otpCode.length !== 4) {
      setError("Please enter the complete 4-digit OTP");
      return;
    }

    const verifyData = {
      receiver: phoneNumber,
      otp: otpCode,
      type: "sms",
    };

    verifyOtpMutation.mutate(verifyData);
  };

  const handleResend = () => {
    if (!phoneNumber) {
      setError("Phone number not found. Please go back and try again.");
      return;
    }

    const otpData = {
      type: "sms",
      receiver: phoneNumber,
    };

    resendOtpMutation.mutate(otpData);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (!phoneNumber) {
    return (
      <>
        <Navbar variant="dark" expand={false} className="py-2 bgNav sticky-top">
          <Container fluid className="px-3">
            <div className="d-flex align-items-center w-100 justify-content-md-start justify-content-center">
              {/* Logo */}
              <Navbar.Brand href="#home">
                <img
                  src="/images/Group 1.png"
                  height="40"
                  className="d-inline-block align-top"
                  alt="HyTech Didactic Logo"
                />
              </Navbar.Brand>
            </div>
          </Container>
        </Navbar>

        <Container
          fluid
          className="d-flex justify-content-center align-items-center pt-5"
        >
          <Card className="phone-modal">
            <Card.Body className="phone-modal__body text-center">
              {/* Warning Icon */}
              <div className="text-center mb-3">
                <div className="phone-modal__icon-wrapper">
                  <FiLock className="phone-modal__lock-icon text-warning" />
                </div>
              </div>

              {/* Title */}
              <h6 className="phone-modal__title text-warning mb-3">
                Session Expired
              </h6>

              {/* Message */}
              <p className="phone-modal__subtitle mb-4">
                Your OTP session has expired or invalid access detected. Please
                start the verification process again.
              </p>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <Button
                  variant="outline-secondary"
                  className="flex-grow-1"
                  onClick={() => navigate("/")}
                >
                  Go Home
                </Button>
                <Button
                  variant="primary"
                  className="flex-grow-1"
                  onClick={() => navigate("/add-mobile")}
                >
                  Start Over
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar variant="dark" expand={false} className="py-2 bgNav sticky-top">
        <Container fluid className="px-3">
          <div className="d-flex align-items-center w-100 justify-content-md-start justify-content-center">
            {/* Logo */}
            <Navbar.Brand href="#home">
              <img
                src="/images/Group 1.png"
                height="40"
                className="d-inline-block align-top"
                alt="HyTech Didactic Logo"
              />
            </Navbar.Brand>
          </div>
        </Container>
      </Navbar>

      <Container
        fluid
        className="d-flex justify-content-center align-items-center pt-5 px-0"
      >
        <Card className="phone-modal">
          <Card.Body className="phone-modal__body">
            {/* Lock Icon */}
            <div className="text-center mb-3">
              <div className="phone-modal__icon-wrapper">
                <FiLock className="phone-modal__lock-icon" />
              </div>
            </div>

            {/* Title */}
            <h6 className="phone-modal__title">Please check your mail box</h6>

            {/* Subtitle */}
            <p className="phone-modal__subtitle">
              We've sent a code to{" "}
              <span className="bold-600">{phoneNumber}</span>
            </p>

            {/* Error Alert */}
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {/* OTP Input Form */}
            <Form onSubmit={handleVerify}>
              {/* OTP Input Group */}
              <div className="d-flex justify-content-center gap-2 mb-1">
                {otp.map((digit, index) => (
                  <Form.Control
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="otp-modal__input text-center"
                    style={{ width: "60px", height: "60px", fontSize: "24px" }}
                    disabled={verifyOtpMutation.isLoading}
                  />
                ))}
              </div>

              {/* Resend Link */}
              <div className="text-center mb-4">
                <span className="otp-modal__resend-text">
                  Didn't get a code?{" "}
                  <Button
                    variant="link"
                    className="otp-modal__resend-button p-0"
                    onClick={handleResend}
                    disabled={
                      resendOtpMutation.isLoading || verifyOtpMutation.isLoading
                    }
                  >
                    {resendOtpMutation.isLoading
                      ? "Sending..."
                      : "Click to resend"}
                  </Button>
                </span>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <Button
                  variant="outline-secondary"
                  className="flex-grow-1"
                  onClick={handleCancel}
                  disabled={
                    verifyOtpMutation.isLoading || resendOtpMutation.isLoading
                  }
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="flex-grow-1"
                  type="submit"
                  disabled={
                    verifyOtpMutation.isLoading ||
                    resendOtpMutation.isLoading ||
                    otp.join("").length !== 4
                  }
                >
                  {verifyOtpMutation.isLoading ? "Verifying..." : "Verify"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default VerifyOtp;
